import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {{ url: string, promise: Promise<Element | null>, controller: AbortController } | null} */
  #pendingProductPage = null;
  /** @type {AbortController | null} */
  #cartUpdateAbortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (this.dataset.presentation === 'card_quick_buy') {
      const currentProductUrl = productCard?.canonicalProductPageUrl || this.dataset.productUrl || '';
      if (!currentProductUrl) return '';

      const canonicalUrl = new URL(currentProductUrl, window.location.origin);
      canonicalUrl.searchParams.delete('variant');
      return canonicalUrl.toString();
    }

    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController?.abort();
    this.#cartUpdateAbortController = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#abortController = null;
    this.#pendingProductPage = null;
    this.#cartUpdateAbortController?.abort();
    this.#cartUpdateAbortController = null;
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#pendingProductPage?.controller.abort();
    this.#pendingProductPage = null;
    this.#abortController = null;
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const requestGeneration = dialogComponent.beginContentRequest();
    const isCurrentRequest = () =>
      this.isConnected && dialogComponent.isCurrentContentRequest(requestGeneration);

    try {
      const productGrid = await this.fetchProductPage(currentUrl);

      if (!productGrid || !isCurrentRequest()) return;

      // Use a fresh clone from the cache.
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);

      if (!isCurrentRequest()) return;

      let variantPickerSource = productGrid;
      if (this.dataset.presentation === 'card_quick_buy') {
        variantPickerSource = /** @type {Element} */ (productGrid.cloneNode(true));
        for (const selectedSizeDetails of variantPickerSource.querySelectorAll(
          '.pdp-size-guide__selected-size-details',
        )) {
          selectedSizeDetails.remove();
        }
      }
      this.#updateVariantPicker(variantPickerSource);
      this.#openQuickAddModal(dialogComponent, requestGeneration);
    } catch (error) {
      if (isCurrentRequest() && error?.name !== 'AbortError') {
        console.error('[quick-add] Unable to load chooser:', error);
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * Opens the shared chooser only if this card still owns the latest content request.
   * @param {QuickAddDialog} dialogComponent
   * @param {number} requestGeneration
   */
  #openQuickAddModal = (dialogComponent, requestGeneration) => {
    const isCurrentRequest = () =>
      this.isConnected && dialogComponent.isCurrentContentRequest(requestGeneration);

    dialogComponent.showDialogWhen(isCurrentRequest, () => {
      const dialog = dialogComponent.refs?.dialog;
      const dialogLabel = this.dataset.dialogLabel?.trim();
      if (dialog && dialogLabel) dialog.setAttribute('aria-label', dialogLabel);

      this.#stayVisibleUntilDialogCloses(dialogComponent);
      requestAnimationFrame(() => {
        if (isCurrentRequest()) this.#resetScroll();
      });

      if (dialog) {
        dialog.addEventListener(
          'animationstart',
          () => {
            if (isCurrentRequest()) this.#resetScroll();
          },
          { once: true }
        );
      }
    });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.invalidateContentRequests();
    dialogComponent.closeDialog();
  };

  /**
   * Prefetches product content without surfacing a rejected promise from an
   * interaction hint such as pointerenter or focusin.
   * @param {string} productPageUrl - The URL of the product page to prefetch
   */
  prefetchProductPage(productPageUrl) {
    const requestUrl = this.dataset.presentation === 'card_quick_buy' ? this.productPageUrl : productPageUrl;
    this.fetchProductPage(requestUrl).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[quick-add] Product prefetch failed:', error);
    });
  }

  /**
   * Fetches and caches the product grid. Repeated pointer, focus and click
   * requests for the same URL share one in-flight request.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Element | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const cachedContent = this.#cachedContent.get(productPageUrl);
    if (cachedContent) return cachedContent;

    if (this.#pendingProductPage?.url === productPageUrl) {
      return this.#pendingProductPage.promise;
    }

    // A card can change variant while prefetching. Only a different URL
    // supersedes the existing request; identical callers share it above.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const productPagePromise = (async () => {
      const response = await fetch(productPageUrl, {
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement || abortController.signal.aborted) return null;

      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      this.#cachedContent.set(productPageUrl, productGrid);
      return productGrid;
    })();

    const pendingProductPage = {
      url: productPageUrl,
      promise: productPagePromise,
      controller: abortController,
    };
    this.#pendingProductPage = pendingProductPage;

    try {
      return await productPagePromise;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === abortController) this.#abortController = null;
      if (this.#pendingProductPage === pendingProductPage) this.#pendingProductPage = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const isCardQuickBuy = this.dataset.presentation === 'card_quick_buy';

    // The fetched PDP already has the page's only H1. The chooser uses its
    // dedicated product title and dialog label instead of duplicating that H1.
    for (const heading of productGrid.querySelectorAll('h1')) heading.remove();
    for (const zoomControl of productGrid.querySelectorAll('.product-media-container__zoom-button, zoom-dialog')) {
      zoomControl.remove();
    }
    // This grid is fetched from the canonical PDP, but the modal is not a PDP
    // placement. Keep the app-owned Wishlist UI scoped to the card and full PDP.
    for (const wishlistPlacement of productGrid.querySelectorAll(
      'aylora-wishlist-placement[data-placement="pdp"]',
    )) {
      wishlistPlacement.remove();
    }
    // The card chooser is intentionally lean. Remove PDP-only content from the
    // DOM, rather than relying on visual hiding, so it also stays out of the
    // dialog accessibility tree.
    if (isCardQuickBuy) {
      for (const pdpOnlyContent of productGrid.querySelectorAll(
        '.pdp-size-guide__selected-size-details, .pdp-product-content, .pdp-short-description, .pdp-fit-note, .rating-wrapper, product-inventory',
      )) {
        pdpOnlyContent.remove();
      }
    }

    const viewMoreDetails = productGrid.querySelector('.view-more-details__wrapper');
    if (isCardQuickBuy && viewMoreDetails instanceof HTMLElement) {
      viewMoreDetails.hidden = false;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (isCardQuickBuy && viewMoreDetails) {
        productGrid.appendChild(viewMoreDetails);
      }

      productDetails?.remove();
    } else if (isCardQuickBuy && viewMoreDetails) {
      const buyButtons = productGrid.querySelector('.buy-buttons-block');
      buyButtons?.insertAdjacentElement('afterend', viewMoreDetails);
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);
    // Set presentation context before inserting custom elements so their
    // connected callbacks see the correct explicit-decision boundary.
    modalContent.toggleAttribute('data-card-quick-buy', isCardQuickBuy);

    morph(modalContent, productGrid);

    // A Product Card internally tracks a first available Variant so its media
    // and URL can stay useful. That internal Variant must not silently resolve
    // an arbitrary Size in the chooser.
    if (!isCardQuickBuy) this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #contentGeneration = 0;
  #selectionGeneration = 0;

  beginContentRequest() {
    this.#cancelActiveVariantRequest();
    this.#selectionGeneration++;
    return ++this.#contentGeneration;
  }

  /** @param {number} generation */
  isCurrentContentRequest(generation) {
    return generation === this.#contentGeneration && this.isConnected && document.getElementById(this.id) === this;
  }

  invalidateContentRequests() {
    this.#cancelActiveVariantRequest();
    this.#contentGeneration++;
    this.#selectionGeneration++;
  }

  #cancelActiveVariantRequest() {
    const picker = this.querySelector('variant-picker');
    picker?.cancelPendingRequest?.();
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.invalidateContentRequests();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    const contentGeneration = this.#contentGeneration;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError || !this.isCurrentContentRequest(contentGeneration)) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    const contentGeneration = this.#contentGeneration;
    const selectionGeneration = ++this.#selectionGeneration;

    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (
          !this.isCurrentContentRequest(contentGeneration) ||
          selectionGeneration !== this.#selectionGeneration
        ) {
          return;
        }
        if (!detail?.html) return;

        const { html } = detail;
        const titleAnchor = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const detailsAnchor = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-more-details'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-more-details'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (this.querySelector('[data-card-quick-buy]')) {
          for (const selectedSizeDetails of this.querySelectorAll('.pdp-size-guide__selected-size-details')) {
            selectedSizeDetails.remove();
          }
        }

        if (detailsAnchor && viewMoreDetailsLink) viewMoreDetailsLink.href = detailsAnchor.href;
        if (titleAnchor && mobileProductTitle) mobileProductTitle.href = titleAnchor.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    this.invalidateContentRequests();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
